import { Caption1, Input, Menu, MenuDivider, MenuItem, MenuItemRadio, MenuList, MenuPopover, MenuTrigger, makeStyles, mergeClasses, tokens } from "@fluentui/react-components";
import { MoreHorizontal16Regular, PeopleCommunity20Regular } from "@fluentui/react-icons";
import { useEffect, useMemo, useState } from "react";
import { AppDialog, ConfirmDialog, DialogActionsBar } from "@/components/app-dialog";
import { useDataTableStyles } from "@/components/data-table";
import { FuiButton, FuiDropdown } from "@/components/fui-control";
import { GhostEmptyState } from "@/components/ghost-empty-state";
import { ListToolbar } from "@/components/list-toolbar";
import type { RaciLetter, StakeholderLevel } from "@/engine/types";
import { useCan } from "@/features/access/use-plan-access";
import { StakeholderHelp } from "@/features/pm/StakeholderHelp";

import { addStakeholder, assignRaci, removeStakeholder, updateStakeholder } from "@/features/pm/pm-ops";
import { RACI_LABEL, RACI_LEGEND, RACI_LETTERS, RACI_MEANING, STAKEHOLDER_LEVELS, STAKEHOLDER_LEVEL_LABEL, letterAt, raciTargets } from "@/features/pm/raci";
import {
  STAKEHOLDER_MOCK_CASES,
  setStakeholderMockCase,
  stakeholderMock,
  useStakeholderMockCase,
  type StakeholderMock,
  type StakeholderMockCase,
} from "@/features/pm/stakeholder-mock";
import { contentShell, listBody, shellGutter } from "@/shell/content-frame";
import { useProjectStore } from "@/store/project-store";
import { newId } from "@/store/tree-ops";

const useStyles = makeStyles({
  page: {
    ...contentShell,
    display: "flex",
    flexDirection: "column",
    height: "100%",
    minHeight: 0,
    backgroundColor: tokens.colorNeutralBackground2,
  },
  body: {
    ...listBody,
    padding: `0 ${shellGutter} 24px`,
    display: "flex",
    flexDirection: "column",
    gap: "20px",
  },
  form: { display: "flex", flexDirection: "column", gap: "8px" },
  clip: { display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  matrix: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  value: {
    display: "inline-flex",
    alignItems: "center",
    height: "24px",
    padding: "0 4px",
    borderRadius: tokens.borderRadiusSmall,
    color: tokens.colorNeutralForeground1,
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
    fontWeight: tokens.fontWeightRegular,
    whiteSpace: "nowrap",
  },
  valueEmpty: { color: tokens.colorNeutralForeground3 },
  valueBtn: {
    margin: "0 -4px",
    border: "none",
    background: "transparent",
    fontFamily: "inherit",
    cursor: "pointer",
    ":hover": { backgroundColor: tokens.colorNeutralBackground1Hover },
    ":active": { backgroundColor: tokens.colorNeutralBackground1Pressed },
    "&[aria-expanded='true']": { backgroundColor: tokens.colorNeutralBackground1Hover },
    ":focus-visible": { outline: `2px solid ${tokens.colorStrokeFocus2}`, outlineOffset: "-2px" },
  },
  choice: {
    "&.fui-MenuItem.fui-MenuItem": {
      height: "auto",
      alignItems: "flex-start",
      whiteSpace: "normal",
    },
  },
  option: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: "2px",
  },
  optionHint: {
    color: tokens.colorNeutralForeground3,
    whiteSpace: "normal",
  },
  quiet: { color: tokens.colorNeutralForeground3 },
  aside: {
    display: "inline-flex",
    alignItems: "center",
    gap: "2px",
  },
  mockBtn: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "24px",
    height: "24px",
    padding: 0,
    border: "none",
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: "transparent",
    color: tokens.colorNeutralForeground3,
    cursor: "pointer",
    flexShrink: 0,
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
      color: tokens.colorNeutralForeground1,
    },
    ":focus-visible": {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: "1px",
    },
  },
});

const LEVEL_OPTIONS = STAKEHOLDER_LEVELS.map((level) => ({ value: level, label: STAKEHOLDER_LEVEL_LABEL[level] }));

interface Draft {
  id?: string;
  name: string;
  org: string;
  role: string;
  interest: StakeholderLevel;
  influence: StakeholderLevel;
}

const blank = (): Draft => ({ name: "", org: "", role: "", interest: "medium", influence: "medium" });

export function ProjectStakeholdersPage() {
  const styles = useStyles();
  const table = useDataTableStyles();
  const project = useProjectStore((state) => state.project);
  const canEdit = useCan("plan.schedule.edit");
  const scenario = useStakeholderMockCase();
  const [draft, setDraft] = useState<Draft | null>(null);
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [local, setLocal] = useState<(StakeholderMock & { scenario: StakeholderMockCase }) | null>(null);
  const taskIds = useMemo(() => raciTargets(project.tasks).map((task) => task.id), [project.tasks]);
  const taskKey = taskIds.join("\n");
  const preview = useMemo(
    () => (scenario === "live" ? null : stakeholderMock(scenario, taskIds)),
    [scenario, taskIds, taskKey],
  );
  useEffect(() => {
    if (!preview) {
      setLocal(null);
      return;
    }
    setLocal({ scenario, stakeholders: preview.stakeholders, raci: preview.raci });
  }, [preview, scenario]);
  const shown: StakeholderMock =
    scenario === "live"
      ? { stakeholders: project.stakeholders ?? [], raci: project.raci ?? [] }
      : local?.scenario === scenario
        ? local
        : (preview ?? { stakeholders: [], raci: [] });
  const people = shown.stakeholders;
  const rows = raciTargets(project.tasks);

  const keepPreview = (next: StakeholderMock) => {
    setLocal({ scenario, stakeholders: next.stakeholders, raci: next.raci });
  };

  const save = () => {
    if (!draft || !draft.name.trim()) return;
    if (scenario !== "live") {
      const base = { ...project, stakeholders: shown.stakeholders, raci: shown.raci };
      const next = draft.id ? updateStakeholder(base, draft.id, draft) : addStakeholder(base, draft, newId("sh"));
      keepPreview({ stakeholders: next.stakeholders ?? [], raci: next.raci ?? [] });
      setDraft(null);
      return;
    }
    const store = useProjectStore.getState();
    if (draft.id) {
      const id = draft.id;
      store.commit("이해관계자 수정", (plan) => updateStakeholder(plan, id, draft));
    } else {
      store.commit("이해관계자 추가", (plan) => addStakeholder(plan, draft, newId("sh")));
    }
    setDraft(null);
  };

  const assign = (taskId: string, stakeholderId: string, letter: RaciLetter | null) => {
    if (!canEdit) return;
    if (scenario !== "live") {
      const base = { ...project, stakeholders: shown.stakeholders, raci: shown.raci };
      const updated = assignRaci(base, taskId, stakeholderId, letter);
      keepPreview({ stakeholders: updated.stakeholders ?? [], raci: updated.raci ?? [] });
      return;
    }
    useProjectStore.getState().commit("RACI", (plan) => assignRaci(plan, taskId, stakeholderId, letter));
  };

  return (
    <div className={styles.page}>
      <ListToolbar
        title="이해관계자"
        description="관심·영향과 WBS 책임"
        titleAside={
          <div className={styles.aside}>
            <StakeholderMockMenu styles={styles} scenario={scenario} onPick={setStakeholderMockCase} />
            <StakeholderHelp />
          </div>
        }
        headingAction={
          canEdit ? (
            <FuiButton appearance="primary" icon={<PeopleCommunity20Regular />} onClick={() => setDraft(blank())}>
              이해관계자 추가
            </FuiButton>
          ) : undefined
        }
      />
      <div className={styles.body}>
        {people.length === 0 ? (
          <GhostEmptyState title="이해관계자가 없습니다" description="추가하면 관심·영향과 책임을 적습니다." />
        ) : (
          <>
            <div className={table.frame}>
              <table className={table.table} aria-label="이해관계자">
                <thead>
                  <tr>
                    <th className={table.th}>이름</th>
                    <th className={table.th}>조직</th>
                    <th className={table.th}>역할</th>
                    <th className={table.th}>관심</th>
                    <th className={table.th}>영향</th>
                    <th className={table.th}>동작</th>
                  </tr>
                </thead>
                <tbody>
                  {people.map((person) => (
                    <tr key={person.id}>
                      <td className={table.td}><span className={styles.clip} title={person.name}>{person.name}</span></td>
                      <td className={table.td}><span className={styles.clip} title={person.org}>{person.org || "—"}</span></td>
                      <td className={table.td}><span className={styles.clip} title={person.role}>{person.role || "—"}</span></td>
                      <td className={table.td}>{STAKEHOLDER_LEVEL_LABEL[person.interest]}</td>
                      <td className={table.td}>{STAKEHOLDER_LEVEL_LABEL[person.influence]}</td>
                      <td className={table.td}>
                        {canEdit ? (
                          <Menu>
                            <MenuTrigger disableButtonEnhancement>
                              <FuiButton appearance="subtle" icon={<MoreHorizontal16Regular />} aria-label="이해관계자 동작" />
                            </MenuTrigger>
                            <MenuPopover>
                              <MenuList>
                                <MenuItem onClick={() => setDraft(person)}>수정</MenuItem>
                                <MenuItem onClick={() => setPendingId(person.id)}>삭제</MenuItem>
                              </MenuList>
                            </MenuPopover>
                          </Menu>
                        ) : <span className={styles.quiet}>—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className={styles.matrix}>
              <Caption1 className={styles.quiet}>{RACI_LEGEND}</Caption1>
              <div className={table.frame}>
                <table className={table.table} aria-label="RACI">
                  <thead>
                    <tr>
                      <th className={table.th}>WBS</th>
                      {people.map((person) => (
                        <th key={person.id} className={table.th}>
                          <span className={styles.clip} title={person.name}>{person.name}</span>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.length === 0 ? (
                      <tr>
                        <td className={table.td} colSpan={people.length + 1}><span className={styles.quiet}>WBS 항목이 없습니다.</span></td>
                      </tr>
                    ) : rows.map((task) => (
                      <tr key={task.id}>
                        <td className={table.td}><span className={styles.clip} title={task.name}>{task.name}</span></td>
                        {people.map((person) => (
                          <td key={person.id} className={table.td}>
                            <RaciCell
                              taskName={task.name}
                              personName={person.name}
                              letter={letterAt(shown.raci, task.id, person.id)}
                              canEdit={canEdit}
                              onPick={(letter) => assign(task.id, person.id, letter)}
                            />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
      <AppDialog
        open={draft != null}
        onClose={() => setDraft(null)}
        title={draft?.id ? "이해관계자 수정" : "이해관계자 추가"}
        footer={
          <DialogActionsBar
            onCancel={() => setDraft(null)}
            onConfirm={save}
            cancelLabel="닫기"
            confirmLabel="저장"
            disabled={!draft?.name.trim()}
          />
        }
      >
        {draft ? (
          <div className={styles.form}>
            <Input aria-label="이름" placeholder="이름" value={draft.name} onChange={(_, data) => setDraft({ ...draft, name: data.value })} />
            <Input aria-label="조직" placeholder="조직" value={draft.org} onChange={(_, data) => setDraft({ ...draft, org: data.value })} />
            <Input aria-label="역할" placeholder="역할" value={draft.role} onChange={(_, data) => setDraft({ ...draft, role: data.value })} />
            <FuiDropdown aria-label="관심" prefix="관심" density="dialog" value={draft.interest} options={LEVEL_OPTIONS} onValueChange={(value) => setDraft({ ...draft, interest: value as StakeholderLevel })} />
            <FuiDropdown aria-label="영향" prefix="영향" density="dialog" value={draft.influence} options={LEVEL_OPTIONS} onValueChange={(value) => setDraft({ ...draft, influence: value as StakeholderLevel })} />
          </div>
        ) : null}
      </AppDialog>
      <ConfirmDialog
        open={pendingId != null}
        title="이해관계자 삭제"
        message="이 이해관계자를 삭제할까요? RACI에서 그 칸도 함께 빠집니다."
        confirmLabel="삭제"
        onCancel={() => setPendingId(null)}
        onConfirm={() => {
          if (pendingId && scenario !== "live") {
            const base = { ...project, stakeholders: shown.stakeholders, raci: shown.raci };
            const next = removeStakeholder(base, pendingId);
            keepPreview({ stakeholders: next.stakeholders ?? [], raci: next.raci ?? [] });
          } else if (pendingId) {
            useProjectStore.getState().commit("이해관계자 삭제", (plan) => removeStakeholder(plan, pendingId));
          }
          setPendingId(null);
        }}
      />
    </div>
  );
}

function RaciCell({
  taskName,
  personName,
  letter,
  canEdit,
  onPick,
}: {
  taskName: string;
  personName: string;
  letter: RaciLetter | null;
  canEdit: boolean;
  onPick: (letter: RaciLetter | null) => void;
}) {
  const styles = useStyles();
  const label = letter ? `${letter} ${RACI_LABEL[letter]}` : "—";
  const className = mergeClasses(styles.value, !letter && styles.valueEmpty);
  if (!canEdit) return <span className={className}>{label}</span>;
  return (
    <Menu
      checkedValues={{ letter: [letter ?? "none"] }}
      onCheckedValueChange={(_, data) => {
        const picked = data.checkedItems[0];
        onPick(picked === "R" || picked === "A" || picked === "C" || picked === "I" ? picked : null);
      }}
      positioning="below-start"
    >
      <MenuTrigger disableButtonEnhancement>
        <button type="button" className={mergeClasses(className, styles.valueBtn)} aria-label={`${taskName} ${personName} ${letter ? `${letter} ${RACI_LABEL[letter]}` : "없음"}`}>
          {label}
        </button>
      </MenuTrigger>
      <MenuPopover>
        <MenuList>
          {RACI_LETTERS.map((item) => (
            <MenuItemRadio key={item} className={styles.choice} name="letter" value={item}>
              <span className={styles.option}>
                <span>{item} {RACI_LABEL[item]}</span>
                <Caption1 className={styles.optionHint}>{RACI_MEANING[item]}</Caption1>
              </span>
            </MenuItemRadio>
          ))}
          <MenuDivider />
          <MenuItemRadio name="letter" value="none">없음</MenuItemRadio>
        </MenuList>
      </MenuPopover>
    </Menu>
  );
}

function StakeholderMockMenu({
  styles,
  scenario,
  onPick,
}: {
  styles: ReturnType<typeof useStyles>;
  scenario: StakeholderMockCase;
  onPick: (id: StakeholderMockCase) => void;
}) {
  return (
    <Menu
      checkedValues={{ case: scenario === "live" ? [] : [scenario] }}
      onCheckedValueChange={(_, data) => {
        const next = data.checkedItems[0];
        if (next === "many" || next === "few" || next === "empty") onPick(next);
      }}
      positioning="below-start"
    >
      <MenuTrigger disableButtonEnhancement>
        <button type="button" className={styles.mockBtn} aria-label="목업 사례" title="목업 사례">
          <MoreHorizontal16Regular />
        </button>
      </MenuTrigger>
      <MenuPopover>
        <MenuList>
          {STAKEHOLDER_MOCK_CASES.map((item) => (
            <MenuItemRadio key={item.id} name="case" value={item.id}>
              {item.label}
            </MenuItemRadio>
          ))}
          <MenuDivider />
          <MenuItem onClick={() => onPick("live")}>초기화</MenuItem>
        </MenuList>
      </MenuPopover>
    </Menu>
  );
}
